import { ParkingZone } from '../entities/Vehicle';
import { ReservationTrajectoryItem } from '../entities/Reservation';
import { DATE_TIME_WITH_SECONDS, formatDateTime } from './datetime-utils';

export const getDisplayAreaOptions = ({
  isArchived,
  isSatellite,
}: {
  isArchived: boolean;
  isSatellite: boolean;
}): google.maps.PolygonOptions => ({
  fillColor: isArchived ? '#8C8C89' : isSatellite ? '#FF0000' : '#00A399',
  strokeWeight: 0,
  draggable: true,
  clickable: true,
});

export const getParkingAreaOptions = ({
  isArchived,
  isSatellite,
}: {
  isArchived: boolean;
  isSatellite: boolean;
}): google.maps.PolygonOptions => ({
  fillColor: isArchived ? '#8C8C89' : isSatellite ? '#FF000050' : '#00A39933',
  strokeColor: isArchived ? '#65615D' : isSatellite ? '#FF0000' : '#008D85',
  strokeWeight: 2,
  draggable: true,
  clickable: true,
});

export type PolygonType = 'display' | 'parking';

export const hideGoogleLogoStyles = {
  'a[href^="http://maps.google.com/maps"]': {
    display: 'none !important',
  },
  'a[href^="https://maps.google.com/maps"]': {
    display: 'none !important',
  },
  '& .gmnoprint a, .gmnoprint span, .gm-style-cc': {
    display: 'none',
  },
};

export const handleParkingZoneMapApiLoaded = (
  map: google.maps.Map,
  maps: typeof google.maps,
  parkingZone: ParkingZone,
  canEdit = false,
  onFeatureAdded:
    | ((polygon: google.maps.Polygon, type: PolygonType) => void)
    | undefined = undefined,
) => {
  const addPolygon = (parkingZone: ParkingZone, type: PolygonType) => {
    const options =
      type === 'display'
        ? getDisplayAreaOptions({
            isArchived: !!parkingZone.deleted,
            isSatellite: canEdit,
          })
        : getParkingAreaOptions({
            isArchived: !!parkingZone.deleted,
            isSatellite: canEdit,
          });
    const polygon =
      type === 'display' ? parkingZone.displayArea : parkingZone.parkingArea;

    const paths: google.maps.LatLng[] = [];
    for (let c = 0, cc = polygon.coordinates[0].length; c < cc; c++) {
      const point = polygon.coordinates[0][c];
      paths.push(new maps.LatLng(point[1], point[0]));
    }
    const newPolygon = new maps.Polygon({
      paths,
      map,
      ...options,
      draggable: canEdit,
      clickable: canEdit,
    });

    if (onFeatureAdded) onFeatureAdded(newPolygon, type);
    return newPolygon;
  };

  const displayPolygon = addPolygon(parkingZone, 'display');
  const parkingPolygon = addPolygon(parkingZone, 'parking');
  const displayBounds = getPolygonBounds(displayPolygon);
  const parkingBounds = getPolygonBounds(parkingPolygon);
  map.fitBounds(displayBounds.union(parkingBounds), 15);
};

export const handleNewParkingZoneMapApiLoaded = (
  map: google.maps.Map,
  maps: typeof google.maps,
  setClearBtn: (btn: HTMLButtonElement) => void,
  setDrawingManager: (
    drawingManager: google.maps.drawing.DrawingManager,
  ) => void,
) => {
  if (!maps.drawing) {
    return;
  }
  const drawingManager = new maps.drawing.DrawingManager({
    drawingMode: maps.drawing.OverlayType.POLYGON,
    drawingControl: true,
    drawingControlOptions: {
      position: maps.ControlPosition.TOP_LEFT,
      drawingModes: [maps.drawing.OverlayType.POLYGON],
    },
    map,
  });
  setDrawingManager(drawingManager);

  const poly_btn = document.createElement('button');
  const buttonize = (btn: HTMLButtonElement) => {
    // Set CSS for the control border.
    btn.type = 'button';
    btn.setAttribute(
      'style',
      `
		background-color: white;
		cursor: pointer;
		padding: 0px 8px;
		margin: 5px;
		margin-left: -5px;
		border-top-right-radius: 5px;
		border-bottom-right-radius: 5px;
		color:black;
		font-size: 0.9em;
		font-weight: bold;
        height:26.4px;
		border:none;
		box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
	`,
    );
    btn.innerHTML = `
      <svg
        width="16"
        height="18"
        viewBox="0 0 16 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
      <path
        d="M6.33333 14.0001C6.55435 14.0001 6.76631 13.9123 6.92259 13.756C7.07887 13.5997 7.16667 13.3878 7.16667 13.1667V8.16675C7.16667 7.94573 7.07887 7.73377 6.92259 7.57749C6.76631 7.42121 6.55435 7.33342 6.33333 7.33342C6.11232 7.33342 5.90036 7.42121 5.74408 7.57749C5.5878 7.73377 5.5 7.94573 5.5 8.16675V13.1667C5.5 13.3878 5.5878 13.5997 5.74408 13.756C5.90036 13.9123 6.11232 14.0001 6.33333 14.0001ZM14.6667 4.00008H11.3333V3.16675C11.3333 2.50371 11.0699 1.86782 10.6011 1.39898C10.1323 0.93014 9.49637 0.666748 8.83333 0.666748H7.16667C6.50363 0.666748 5.86774 0.93014 5.3989 1.39898C4.93006 1.86782 4.66667 2.50371 4.66667 3.16675V4.00008H1.33333C1.11232 4.00008 0.900358 4.08788 0.744078 4.24416C0.587797 4.40044 0.5 4.6124 0.5 4.83342C0.5 5.05443 0.587797 5.26639 0.744078 5.42267C0.900358 5.57895 1.11232 5.66675 1.33333 5.66675H2.16667V14.8334C2.16667 15.4965 2.43006 16.1323 2.8989 16.6012C3.36774 17.07 4.00363 17.3334 4.66667 17.3334H11.3333C11.9964 17.3334 12.6323 17.07 13.1011 16.6012C13.5699 16.1323 13.8333 15.4965 13.8333 14.8334V5.66675H14.6667C14.8877 5.66675 15.0996 5.57895 15.2559 5.42267C15.4122 5.26639 15.5 5.05443 15.5 4.83342C15.5 4.6124 15.4122 4.40044 15.2559 4.24416C15.0996 4.08788 14.8877 4.00008 14.6667 4.00008ZM6.33333 3.16675C6.33333 2.94573 6.42113 2.73377 6.57741 2.57749C6.73369 2.42121 6.94565 2.33341 7.16667 2.33341H8.83333C9.05435 2.33341 9.26631 2.42121 9.42259 2.57749C9.57887 2.73377 9.66667 2.94573 9.66667 3.16675V4.00008H6.33333V3.16675ZM12.1667 14.8334C12.1667 15.0544 12.0789 15.2664 11.9226 15.4227C11.7663 15.579 11.5543 15.6667 11.3333 15.6667H4.66667C4.44565 15.6667 4.23369 15.579 4.07741 15.4227C3.92113 15.2664 3.83333 15.0544 3.83333 14.8334V5.66675H12.1667V14.8334ZM9.66667 14.0001C9.88768 14.0001 10.0996 13.9123 10.2559 13.756C10.4122 13.5997 10.5 13.3878 10.5 13.1667V8.16675C10.5 7.94573 10.4122 7.73377 10.2559 7.57749C10.0996 7.42121 9.88768 7.33342 9.66667 7.33342C9.44565 7.33342 9.23369 7.42121 9.07741 7.57749C8.92113 7.73377 8.83333 7.94573 8.83333 8.16675V13.1667C8.83333 13.3878 8.92113 13.5997 9.07741 13.756C9.23369 13.9123 9.44565 14.0001 9.66667 14.0001Z"
        fill="#10171B"
          />
      </svg>
    `;

    btn.addEventListener('mouseover', () => {
      btn.style.background = 'rgb(235,235,235)';
    });
    btn.addEventListener('mouseout', () => {
      btn.style.background = 'white';
    });
    setClearBtn(btn);
  };
  // add styles
  buttonize(poly_btn);
  map.controls[maps.ControlPosition.TOP_LEFT].push(poly_btn);
};

export function handleRideTrajectoryMapChange(
  map: google.maps.Map,
  trajectoryItems: ReservationTrajectoryItem[],
  isActive: boolean,
  listener?: google.maps.MapsEventListener,
) {
  if (!trajectoryItems || trajectoryItems.length === 0) return;
  if (listener) google.maps.event.removeListener(listener);
  map.data.forEach(feat => map.data.remove(feat));
  const endPoint = trajectoryItems[0];
  const startPoint = trajectoryItems[trajectoryItems.length - 1];
  const [endLng, endLat] = endPoint.location.coordinates;
  const [startLng, startLat] = startPoint.location.coordinates;

  const pickedTrajectoryItems = trajectoryItems.reduce((result, point, idx) => {
    if (idx % 5 === 0) result.push(point);
    return result;
  }, [] as ReservationTrajectoryItem[]);

  const geoJson = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {
          pointNr: 'A',
          time: startPoint.time,
        },
        geometry: {
          type: 'Point',
          coordinates: [startLng, startLat],
        },
      },
      {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: trajectoryItems.map(it => it.location.coordinates),
        },
      },
      ...pickedTrajectoryItems.reverse().map(point => ({
        type: 'Feature',
        properties: {
          time: point.time,
        },
        geometry: {
          type: 'Point',
          coordinates: [
            point.location.coordinates[0],
            point.location.coordinates[1],
          ],
        },
      })),
    ],
  };
  if (!isActive) {
    geoJson.features.push({
      type: 'Feature',
      properties: {
        pointNr: 'B',
        time: endPoint.time,
      },
      geometry: {
        type: 'Point',
        coordinates: [endLng, endLat],
      },
    });
  }

  const infoWindow = new google.maps.InfoWindow({});

  const newListener = map.data.addListener(
    'click',
    ({ feature }: google.maps.Data.MouseEvent) => {
      const geometry = feature.getGeometry();
      if (geometry?.getType() !== 'Point') return;
      const point = geometry as google.maps.Data.Point;
      infoWindow.setContent(
        formatDateTime(feature.getProperty('time'), DATE_TIME_WITH_SECONDS),
      );
      infoWindow.setPosition(point.get());
      infoWindow.open(map);
    },
  );

  map.data.addGeoJson(geoJson);
  map.data.setStyle(feature => {
    if (feature.getGeometry()?.getType() === 'LineString') {
      return {
        strokeWeight: 1,
        strokeColor: 'red',
      };
    }
    if (feature.getGeometry()?.getType() === 'Point') {
      const pointNr = feature.getProperty('pointNr');
      const time = feature.getProperty('time');
      return {
        label: pointNr ? pointNr : undefined,
        title: formatDateTime(time, DATE_TIME_WITH_SECONDS) ?? undefined,
        icon: pointNr
          ? undefined
          : {
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: 'red',
              fillOpacity: 0.6,
              strokeWeight: 0,
              scale: 3,
            },
      };
    }
    return {};
  });
  const bounds = new google.maps.LatLngBounds();
  map.data.forEach(feature => {
    feature.getGeometry()?.forEachLatLng(latlng => {
      bounds.extend(latlng);
    });
  });
  map.fitBounds(bounds);
  return newListener;
}

export const convertPolygonToGeoJSON = (polygon: google.maps.Polygon) => {
  const geoJSON = {
    type: 'Polygon',
    coordinates: [],
  };

  const paths = polygon.getPaths().getArray();

  paths.forEach(path => {
    const pathArray = [];
    const points = path.getArray();
    const firstPoint = points[0];

    points.forEach(point => {
      pathArray.push([point.lng(), point.lat()]);
    });

    pathArray.push([firstPoint.lng(), firstPoint.lat()]);

    geoJSON.coordinates.push(pathArray as never);
  });

  return geoJSON;
};

const getPolygonBounds: (
  newPolygon: google.maps.Polygon,
) => google.maps.LatLngBounds = (newPolygon: google.maps.Polygon) => {
  const bounds = new google.maps.LatLngBounds();
  newPolygon.getPath().forEach(element => {
    bounds.extend(element);
  });
  return bounds;
};
